import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const COLORS = ['#7c3aed','#db2777','#2563eb','#059669','#d97706','#dc2626','#0891b2'];
const color = (i) => COLORS[i % COLORS.length];

function ordinal(n) {
  if (n === 1) return '1st'; if (n === 2) return '2nd'; if (n === 3) return '3rd';
  return `${n}th`;
}

export default function TodayBirthdays() {
  const [students, setStudents] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [sending,  setSending]  = useState(false);   // bulk send in progress
  const [result,   setResult]   = useState(null);
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    api.list('birthdays/today')
      .then(d => setStudents(d || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Bulk: send to ALL today's birthday students
  const sendAll = async () => {
    if (!window.confirm(`Send WhatsApp birthday wishes to parents of all ${students.length} student(s)?`)) return;
    setSending(true);
    setResult(null);
    try {
      const data = await api.create('birthdays/send-wishes', {});
      setResult({ ok: true, msg: data.message || `Sent to ${data.sent} parent(s)` });
    } catch (e) {
      setResult({ ok: false, msg: e.message || 'Failed to send' });
    } finally {
      setSending(false);
    }
  };

  const today = (() => { const d = new Date(); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })();

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            🎂 Today's Birthdays
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">{today}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {!loading && (
            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
              {students.length} {students.length === 1 ? 'Birthday' : 'Birthdays'} Today
            </span>
          )}
          {isAdmin && !loading && students.length > 0 && (
            <button onClick={sendAll} disabled={sending}
              className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors">
              {sending ? '⏳ Sending…' : '💬 Send Wishes to All'}
            </button>
          )}
        </div>
      </div>

      {result && (
        <div className={`mb-4 px-4 py-2.5 rounded-lg text-sm font-medium border ${result.ok ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
          {result.ok ? '✅' : '❌'} {result.msg}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400 text-sm">Loading…</div>
      ) : students.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center py-20 gap-3">
          <span className="text-5xl">🎈</span>
          <p className="text-gray-500 font-medium">No birthdays today</p>
          <p className="text-gray-400 text-sm">Check back tomorrow!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {students.map((s, i) => (
            <BirthdayCard key={s.id} student={s} accent={color(i)} isAdmin={isAdmin} />
          ))}
        </div>
      )}
    </div>
  );
}

function BirthdayCard({ student, accent, isAdmin }) {
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [err,     setErr]     = useState('');

  const initials  = `${student.firstName?.[0] || ''}${student.lastName?.[0] || ''}`.toUpperCase();
  const className = [student.className, student.section].filter(Boolean).join(' ');

  const sendToThis = async () => {
    setSending(true);
    setErr('');
    try {
      await api.create('birthdays/send-wishes', { studentId: student.id });
      setSent(true);
    } catch (e) {
      setErr(e.message || 'Failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <div className="h-2" style={{ backgroundColor: accent }} />

      <div className="p-5 flex flex-col items-center text-center gap-3">
        <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold shadow"
          style={{ backgroundColor: accent }}>
          {initials || '?'}
        </div>

        <div>
          <p className="font-bold text-gray-800 text-base leading-tight">
            {student.firstName} {student.lastName}
          </p>
          {className && <p className="text-xs text-gray-500 mt-0.5">{className}</p>}
          <p className="text-xs text-gray-400 mt-0.5">Adm: {student.admissionNumber}</p>
        </div>

        <div className="px-3 py-1 rounded-full text-xs font-semibold text-white"
          style={{ backgroundColor: accent }}>
          Turning {ordinal(student.age || 0)}
        </div>

        <div className="w-full bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
          <p className="text-amber-700 text-xs font-medium">🎉 Happy Birthday!</p>
        </div>

        {/* Per-student WhatsApp button — admin only */}
        {isAdmin && (
          sent ? (
            <div className="w-full text-center text-xs font-semibold text-green-600">✅ Wish Sent!</div>
          ) : err ? (
            <div className="w-full text-center text-xs text-red-500">{err}</div>
          ) : (
            <button onClick={sendToThis} disabled={sending}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors">
              {sending ? '⏳ Sending…' : '💬 Send WhatsApp Wish'}
            </button>
          )
        )}
      </div>
    </div>
  );
}
